import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UserDto } from "../api/identity";
import Splash from "../components/shared/Splash";
import { AuthenticationContext } from "../contexts/AuthenticationContext";
import { AppUser, UserContext } from "../contexts/UserContext";
import { fetchActivityBadgeData } from "../slices/ActivitySlice";
import { fetchSharedRequirements } from "../slices/SharedSlice";
import { RootState } from "../slices/Store";
import { getAuthManager } from "../utils/auth";
import { ErrorDetailContainer, toErrorDetailContainer, USER_ACCESS_DENIED_EVENT } from "../api/config";
import { UserService } from "../api/psc";
import UserAccessError from "../components/UserAccessError";

export default function AuthenticationProvider(props: {
    children: JSX.Element | JSX.Element[];
}) {
    const dispatch = useDispatch();

    const [user, setUser] = useState<UserDto | null>(null);
    const [isAuthenticationRequired, setIsAuthenticationRequired] =
        useState(false);

    const sharedState = useSelector((state: RootState) => state.shared);
    const [setupComplete, setSetupComplete] = useState(false);
    const [setupError, setSetupError] = useState<ErrorDetailContainer | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const inProgress = useRef(false);

    const requireAuthenticatedUser = useCallback(() => {
        setIsAuthenticationRequired(true);
    }, []);

    const authenticationContextValue = { requireAuthenticatedUser };

    const fetchRequirements = useCallback(async () => {
        if (inProgress.current) return;
        inProgress.current = true;
        setIsLoading(true);
        setSetupError(null);
        setSetupComplete(false);
        const auth = getAuthManager();
        try {
            await auth.initialize();
            if (!(await auth.getAccessToken())) return;
            await UserService.setup("1");
            await fetchSharedRequirements(dispatch);
            await fetchActivityBadgeData(dispatch);
            setSetupComplete(true);
        } catch (error) {
            setSetupError(toErrorDetailContainer(error));
        } finally {
            setIsLoading(false);
            inProgress.current = false;
        }
    }, [dispatch]);

    useEffect(() => {
        if (isAuthenticationRequired) {
            fetchRequirements();
        }
    }, [isAuthenticationRequired, fetchRequirements]);

    useEffect(() => {
        setUser(sharedState.userProfile);
    }, [sharedState.userProfile]);

    useEffect(() => {
        const onAccessDenied = (event: Event) => {
            if (isAuthenticationRequired) {
                setSetupError((event as CustomEvent<ErrorDetailContainer>).detail);
                setSetupComplete(false);
            }
        };
        window.addEventListener(USER_ACCESS_DENIED_EVENT, onAccessDenied);
        return () => window.removeEventListener(USER_ACCESS_DENIED_EVENT, onAccessDenied);
    }, [isAuthenticationRequired]);

    if (isAuthenticationRequired && setupError) {
        return <UserAccessError error={setupError} isLoading={isLoading} onRetry={fetchRequirements}
            onLogout={() => { getAuthManager().logout().catch(error => setSetupError(toErrorDetailContainer(error))); }} />;
    }

    if (isAuthenticationRequired && (!user || !setupComplete)) {
        return <Splash />;
    }

    return (
        <AuthenticationContext.Provider value={authenticationContextValue}>
            {isAuthenticationRequired && (
                <UserContext.Provider value={new AppUser(user)}>
                    {props.children}
                </UserContext.Provider>
            )}

            {!isAuthenticationRequired && props.children}
        </AuthenticationContext.Provider>
    );
}
